/**
 * Profile content creation helpers
 */
import type { ProfileContent, DAGObject } from '../dag/types.js';
import type { Identity } from '../identity/identity.js';

export interface CreateProfileOptions {
  displayName: string;
  bio?: string;
  avatarDataUrl?: string;
}

/**
 * Create a new profile object
 */
export function createProfile(
  identity: Identity,
  options: CreateProfileOptions
): DAGObject<ProfileContent> {
  const content: ProfileContent = {
    displayName: options.displayName,
    bio: options.bio,
    avatarDataUrl: options.avatarDataUrl,
  };

  return identity.createObject('profile', content);
}

/**
 * Check if object is a profile
 */
export function isProfile(obj: DAGObject): obj is DAGObject<ProfileContent> {
  return obj.type === 'profile';
}

/**
 * Get the latest profile from a list of profiles (by timestamp)
 */
export function getLatestProfile(
  profiles: DAGObject<ProfileContent>[]
): DAGObject<ProfileContent> | null {
  if (profiles.length === 0) return null;
  return profiles.sort((a, b) => b.timestamp - a.timestamp)[0];
}

/**
 * Get display name with fallback to truncated public key
 */
export function getDisplayNameOrFallback(
  profile: DAGObject<ProfileContent> | null,
  pubkey: string
): string {
  if (profile?.content?.displayName) {
    return profile.content.displayName;
  }
  // Fallback to truncated public key
  if (!pubkey) return 'Unknown';
  return `${pubkey.slice(0, 6)}...${pubkey.slice(-4)}`;
}
